/**
 * Proposal Assistant Service
 * 
 * AI-powered proposal preparation - orchestrates methodology generation,
 * CV matching for Associates and IOD Staff in parallel.
 * 
 * Connects to n8n workflow for methodology generation with QA loop.
 * Polls database for results since n8n workflow takes 1.5-3 minutes.
 */

import axios from 'axios';
import { matchCVsFromTor, CVMatchingResult } from './cvMatching';
import { extractTorForCVs, TorForCVsExtractionResult } from './torForCVs';

// n8n webhook URL
const N8N_WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL || 'https://n8n.srv930712.hstgr.cloud/webhook/proposal-methodology';

// API URL for polling
const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Methodology generation result from n8n workflow
 */
export interface MethodologyResult {
    status: 'success' | 'failed' | 'pending';
    sessionId: string;
    fileName?: string;
    message?: string;
    validationScore?: number;
    alignmentScore: number;
    wordCount: number;
    citations?: number;
    cost?: number;
    iterations: number;
    generatedText?: string;
    // From content_json
    qa_summary?: string;
    qa_gaps?: Array<{
        category: string;
        description: string;
        tor_reference?: string;
        severity: 'high' | 'medium' | 'low';
    }>;
    qa_strengths?: string[];
    // Cost estimation data
    estimatedDays?: number;
    investmentBand?: string;
    roleDays?: {
        principal: number;
        senior: number;
        consultant: number;
        analyst: number;
    };
    costBreakdown?: {
        currency: string;
        direct_consultant_cost: number;
        travel_accommodation: number;
        sub_contractors: number;
        overhead: number;
        contingency: number;
        total_low: number;
        total_central: number;
        total_high: number;
    };
    // Source data
    sourceProposals?: string[];
    evaluationQuestions?: string[];
    // Project overview data (from database)
    projectTitle?: string;
    clientName?: string;
    evaluationType?: string;
    securityContext?: string;
    timelinePressure?: string;
    numberOfCountries?: number;
    mentionedCountries?: string[];
    budgetFit?: string;
    budgetCeiling?: number;
    budgetCurrency?: string;
    budgetGap?: number;
    budgetGapPercent?: number;
    estimationConfidence?: string;
    loeSummary?: string;
    loeExplanation?: string;
    daysBreakdown?: {
        inception: number;
        data_collection: number;
        analysis: number;
        reporting: number;
        base_total: number;
    };
    bandColor?: string;
}

/**
 * Combined result from all parallel operations
 */
export interface ProposalAssistantResult {
    sessionId?: string; // Session ID for retry/polling
    methodology: MethodologyResult | null;
    associateCVs: CVMatchingResult | null;
    iodStaffCVs: CVMatchingResult | null;
    torExtraction: TorForCVsExtractionResult | null;
    errors: {
        methodology?: string;
        associateCVs?: string;
        iodStaffCVs?: string;
        torExtraction?: string;
    };
}

/**
 * Progress state for each operation
 */
export interface ProposalAssistantProgress {
    torExtraction: ProgressState;
    methodology: ProgressState;
    associateCVs: ProgressState;
    iodStaffCVs: ProgressState;
}

export interface ProgressState {
    status: 'idle' | 'running' | 'complete' | 'error';
    progress: number; // 0-100
    message: string;
    startTime?: number;
    endTime?: number;
}

/**
 * Requirement tier settings for CV matching
 */
export interface RequirementTiers {
    geographic_countries: 'required' | 'preferred' | 'bonus';
    geographic_regions: 'required' | 'preferred' | 'bonus';
    research_evaluation: 'required' | 'preferred' | 'bonus';
    technical_domain: 'required' | 'preferred' | 'bonus';
    analytical_tools: 'required' | 'preferred' | 'bonus';
    frameworks: 'required' | 'preferred' | 'bonus';
    communication: 'required' | 'preferred' | 'bonus';
    professional_ethical: 'required' | 'preferred' | 'bonus';
    languages: 'required' | 'preferred' | 'bonus';
    sectors: 'required' | 'preferred' | 'bonus';
    donor_experience: 'required' | 'preferred' | 'bonus';
    education: 'required' | 'preferred' | 'bonus';
    experience_years: 'required' | 'preferred' | 'bonus';
    [key: string]: 'required' | 'preferred' | 'bonus';
}

/**
 * Default tier settings - all required
 */
export const DEFAULT_TIERS: RequirementTiers = {
    geographic_countries: 'required',
    geographic_regions: 'required',
    research_evaluation: 'required',
    technical_domain: 'required',
    analytical_tools: 'required',
    frameworks: 'required',
    communication: 'required',
    professional_ethical: 'required',
    languages: 'required',
    sectors: 'required',
    donor_experience: 'required',
    education: 'required',
    experience_years: 'required'
};

/**
 * Convert a File to base64 string (without data URL prefix)
 */
export async function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result as string;
            // Remove the data:application/pdf;base64, prefix
            const base64 = result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = (error) => reject(error);
    });
}

/**
 * Extract text from PDF file for display/preview
 */
export async function extractTextFromFile(file: File): Promise<string> {
    // For now, we'll let the backend handle PDF extraction
    // This is just for the file info
    return `File: ${file.name} (${formatFileSize(file.size)})`;
}

/**
 * ToR extraction summary for list view
 */
export interface TorExtractionSummary {
    session_id: string;
    project_title: string | null;
    client_name: string | null;
    source_filename: string | null;
    uploaded_by: string | null;
    uploaded_at: string;
    status: string;
    has_methodology: boolean;
    has_cv_results: boolean;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Generate a unique session ID (GUID format required by database)
 */
export function generateSessionId(): string {
    // Generate a proper UUID v4 format for SQL Server UNIQUEIDENTIFIER
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * List all ToR extractions from the database
 * @param searchQuery - Optional search query
 * @param limit - Max results (default 50)
 */
export async function listTorExtractions(searchQuery?: string, limit: number = 50): Promise<TorExtractionSummary[]> {
    try {
        const params = new URLSearchParams();
        if (searchQuery) params.append('q', searchQuery);
        if (limit) params.append('limit', limit.toString());
        
        const response = await axios.get(
            `${API_URL}/tor-extractions?${params.toString()}`,
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
                timeout: 15000
            }
        );
        
        return response.data.extractions || [];
    } catch (error) {
        console.error('[Proposal Assistant] Failed to list ToR extractions:', error);
        throw new Error('Failed to load existing ToRs');
    }
}

/**
 * Save a ToR extraction to the database
 * @param extractionContent - The extracted ToR content
 * @param projectTitle - Project title from ToR
 * @param clientName - Client name from ToR
 * @param sourceFilename - Original filename
 * @param sessionId - Optional session ID (generated if not provided)
 * @returns Session ID
 */
export async function saveTorExtraction(
    extractionContent: any,
    projectTitle?: string,
    clientName?: string,
    sourceFilename?: string,
    sessionId?: string
): Promise<string> {
    try {
        const response = await axios.post(
            `${API_URL}/save-tor-extraction`,
            {
                session_id: sessionId,
                extraction_content: extractionContent,
                project_title: projectTitle,
                client_name: clientName,
                source_filename: sourceFilename
            },
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
                timeout: 15000
            }
        );
        
        console.log('[Proposal Assistant] Saved ToR extraction:', response.data.session_id);
        return response.data.session_id;
    } catch (error) {
        console.error('[Proposal Assistant] Failed to save ToR extraction:', error);
        throw new Error('Failed to save ToR extraction');
    }
}

/**
 * Delete a ToR extraction
 * @param sessionId - Session ID to delete
 */
export async function deleteTorExtraction(sessionId: string): Promise<void> {
    try {
        console.log(`[Proposal Assistant] Deleting ToR extraction: ${sessionId}`);
        const response = await axios.delete(
            `${API_URL}/tor-extractions/${sessionId}`,
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
                timeout: 15000
            }
        );
        console.log(`[Proposal Assistant] Delete response:`, response.status, response.data);
    } catch (error: any) {
        const status = error?.response?.status;
        const message = error?.response?.data?.error || error?.message || 'Unknown error';
        console.error(`[Proposal Assistant] Failed to delete ToR extraction (${status}):`, message);
        throw new Error(status === 404 ? 'ToR not found' : `Failed to delete ToR: ${message}`);
    }
}

/**
 * Get full ToR extraction data for reuse
 * @param sessionId - Session ID to fetch
 */
export async function getFullTorExtraction(sessionId: string): Promise<any> {
    try {
        const response = await axios.get(
            `${API_URL}/tor-extractions/${sessionId}/full`,
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
                timeout: 30000
            }
        );
        
        return response.data.sections;
    } catch (error) {
        console.error('[Proposal Assistant] Failed to get full ToR extraction:', error);
        throw new Error('Failed to load ToR data');
    }
}

/**
 * Poll for methodology result from database
 * 
 * @param sessionId - Session ID to poll for
 * @returns Methodology result or null if not ready
 */
export async function pollForMethodologyResult(sessionId: string): Promise<MethodologyResult | null> {
    try {
        const response = await axios.get(
            `${API_URL}/proposal-section/${sessionId}/methodology`,
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
                timeout: 10000
            }
        );
        
        if (response.data.found && response.data.section) {
            const section = response.data.section;
            const content = section.content_json || {};
            
            // Parse role_days_json and cost_breakdown_json if they exist
            let roleDays = undefined;
            let costBreakdown = undefined;
            
            if (section.role_days_json) {
                try {
                    roleDays = typeof section.role_days_json === 'string' 
                        ? JSON.parse(section.role_days_json) 
                        : section.role_days_json;
                } catch (e) {
                    console.warn('[Proposal Assistant] Failed to parse role_days_json:', e);
                }
            }
            
            if (section.cost_breakdown_json) {
                try {
                    costBreakdown = typeof section.cost_breakdown_json === 'string'
                        ? JSON.parse(section.cost_breakdown_json)
                        : section.cost_breakdown_json;
                } catch (e) {
                    console.warn('[Proposal Assistant] Failed to parse cost_breakdown_json:', e);
                }
            }
            
            // Parse source_proposals - could be JSON array string or comma-separated
            let sourceProposals: string[] = [];
            if (section.source_proposals) {
                try {
                    if (typeof section.source_proposals === 'string') {
                        // Try JSON parse first
                        if (section.source_proposals.startsWith('[')) {
                            sourceProposals = JSON.parse(section.source_proposals);
                        } else {
                            // Comma-separated fallback
                            sourceProposals = section.source_proposals.split(',').map((s: string) => s.trim());
                        }
                    } else if (Array.isArray(section.source_proposals)) {
                        sourceProposals = section.source_proposals;
                    }
                } catch (e) {
                    console.warn('[Proposal Assistant] Failed to parse source_proposals:', e);
                }
            }
            
            // Parse evaluation_questions from content_json or dedicated column
            let evaluationQuestions: string[] = [];
            const eqSource = content.evaluation_questions || content.evaluationQuestions || section.evaluation_questions;
            if (eqSource) {
                try {
                    if (typeof eqSource === 'string' && eqSource.startsWith('[')) {
                        evaluationQuestions = JSON.parse(eqSource);
                    } else if (Array.isArray(eqSource)) {
                        evaluationQuestions = eqSource;
                    }
                } catch (e) {
                    console.warn('[Proposal Assistant] Failed to parse evaluation_questions:', e);
                }
            }
            
            // Parse mentioned_countries if it's a JSON string
            let mentionedCountries: string[] = [];
            if (section.mentioned_countries_json) {
                try {
                    mentionedCountries = typeof section.mentioned_countries_json === 'string'
                        ? JSON.parse(section.mentioned_countries_json)
                        : section.mentioned_countries_json;
                } catch (e) {
                    console.warn('[Proposal Assistant] Failed to parse mentioned_countries_json:', e);
                }
            }
            
            return {
                status: 'success',
                sessionId,
                alignmentScore: content.alignment_score || 0,
                wordCount: section.word_count || content.generatedText?.split(/\s+/).length || 0,
                iterations: content.iterations || 1,
                generatedText: content.generatedText || '',
                qa_summary: content.qa_summary || '',
                qa_gaps: content.qa_gaps || [],
                qa_strengths: content.qa_strengths || [],
                validationScore: section.validation_score || content.alignment_score || 0,
                // Cost estimation data
                estimatedDays: section.estimated_days,
                investmentBand: section.investment_band,
                roleDays,
                costBreakdown,
                // Source data
                sourceProposals,
                evaluationQuestions,
                // Project overview data
                projectTitle: section.project_title,
                clientName: section.client_name,
                evaluationType: section.evaluation_type,
                securityContext: section.security_context,
                timelinePressure: section.timeline_pressure,
                numberOfCountries: section.number_of_countries,
                mentionedCountries,
                budgetFit: section.budget_fit,
                budgetCeiling: section.budget_ceiling,
                budgetCurrency: section.budget_currency,
                budgetGap: section.budget_gap,
                budgetGapPercent: section.budget_gap_percent,
                estimationConfidence: section.estimation_confidence,
                loeSummary: section.loe_summary,
                loeExplanation: section.loe_explanation,
                daysBreakdown: section.days_breakdown_json ? (
                    typeof section.days_breakdown_json === 'string' 
                        ? JSON.parse(section.days_breakdown_json) 
                        : section.days_breakdown_json
                ) : undefined,
                bandColor: section.band_color
            };
        }
        
        return null; // Not ready yet
        
    } catch (error) {
        console.error('[Proposal Assistant] Poll error:', error);
        return null;
    }
}

/**
 * Trigger n8n workflow for methodology generation
 * Fires and forgets - we poll the database for results
 * 
 * @param pdfBase64 - Base64 encoded PDF content
 * @param sessionId - Unique session identifier
 * @param userEmail - User's email address
 * @param fileName - Original file name
 */
export async function triggerN8nWorkflow(
    pdfBase64: string,
    sessionId: string,
    userEmail: string,
    fileName: string
): Promise<void> {
    try {
        console.log('[Proposal Assistant] Triggering n8n methodology workflow...');
        console.log(`[Proposal Assistant] Session: ${sessionId}, File: ${fileName}`);
        
        // Fire and forget - don't wait for response
        // n8n will save results to database when complete
        axios.post(
            N8N_WEBHOOK_URL,
            {
                pdfBase64,
                sessionId,
                userEmail,
                fileName
            },
            {
                headers: { 'Content-Type': 'application/json' },
                timeout: 5000 // Short timeout - we just need to trigger it
            }
        ).catch(err => {
            // Ignore timeout errors - workflow is still running
            if (err.code !== 'ECONNABORTED') {
                console.error('[Proposal Assistant] n8n trigger error:', err.message);
            }
        });
        
        console.log('[Proposal Assistant] n8n workflow triggered successfully');
        
    } catch (error) {
        console.error('[Proposal Assistant] Failed to trigger n8n workflow:', error);
        throw new Error('Failed to start methodology generation. Please try again.');
    }
}

/**
 * Generate methodology with polling
 * Triggers n8n and polls database until result is ready
 * 
 * @param pdfBase64 - Base64 encoded PDF content
 * @param sessionId - Unique session identifier
 * @param userEmail - User's email address
 * @param fileName - Original file name
 * @param onProgress - Progress callback
 * @param maxWaitMs - Maximum wait time (default: 5 minutes)
 * @returns Methodology generation result
 */
export async function generateMethodologyWithPolling(
    pdfBase64: string,
    sessionId: string,
    userEmail: string,
    fileName: string,
    onProgress: (progress: number, message: string) => void,
    maxWaitMs: number = 300000
): Promise<MethodologyResult> {
    // Trigger the n8n workflow
    await triggerN8nWorkflow(pdfBase64, sessionId, userEmail, fileName);
    
    const startTime = Date.now();
    const pollIntervalMs = 5000; // Poll every 5 seconds
    let lastProgress = 10;
    
    onProgress(10, 'Workflow started...');
    
    while (Date.now() - startTime < maxWaitMs) {
        // Wait before polling
        await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
        
        // Update progress based on elapsed time
        const elapsed = Date.now() - startTime;
        const estimatedTotal = 120000; // 2 minutes estimate
        lastProgress = Math.min(90, Math.floor(10 + (elapsed / estimatedTotal) * 80));
        
        let message = 'Processing ToR...';
        if (elapsed > 20000) message = 'Extracting requirements...';
        if (elapsed > 40000) message = 'Searching similar proposals...';
        if (elapsed > 60000) message = 'Generating methodology...';
        if (elapsed > 90000) message = 'Running QA validation...';
        if (elapsed > 120000) message = 'Finalizing...';
        
        onProgress(lastProgress, message);
        
        // Poll for result
        const result = await pollForMethodologyResult(sessionId);
        
        if (result) {
            onProgress(100, 'Complete!');
            return result;
        }
    }
    
    // Timeout - but workflow might still complete
    throw new Error('Methodology generation is taking longer than expected. Please check back in a few minutes.');
}

/**
 * Run the complete Proposal Assistant workflow
 * 
 * Executes in parallel:
 * 1. ToR extraction (fast) → then CV matching for both sources
 * 2. n8n methodology generation (slow)
 * 
 * @param file - PDF file to process
 * @param userEmail - User's email
 * @param onProgress - Callback for progress updates
 * @returns Combined results from all operations
 */
export async function runProposalAssistant(
    file: File,
    userEmail: string,
    onProgress: (progress: ProposalAssistantProgress) => void
): Promise<ProposalAssistantResult> {
    const sessionId = generateSessionId();
    
    // Initialize progress state
    const progress: ProposalAssistantProgress = {
        torExtraction: { status: 'idle', progress: 0, message: 'Waiting to start...' },
        methodology: { status: 'idle', progress: 0, message: 'Waiting to start...' },
        associateCVs: { status: 'idle', progress: 0, message: 'Waiting for ToR extraction...' },
        iodStaffCVs: { status: 'idle', progress: 0, message: 'Waiting for ToR extraction...' }
    };
    
    // Initialize result
    const result: ProposalAssistantResult = {
        methodology: null,
        associateCVs: null,
        iodStaffCVs: null,
        torExtraction: null,
        errors: {}
    };
    
    // Helper to update progress
    const updateProgress = (
        key: keyof ProposalAssistantProgress,
        update: Partial<ProgressState>
    ) => {
        progress[key] = { ...progress[key], ...update };
        onProgress({ ...progress });
    };
    
    try {
        // Convert PDF to base64 for n8n
        console.log('[Proposal Assistant] Converting PDF to base64...');
        const pdfBase64 = await fileToBase64(file);
        console.log(`[Proposal Assistant] PDF converted: ${pdfBase64.length} chars`);
        
        // Start all operations in parallel
        const operations: Promise<void>[] = [];
        
        // ============================================
        // OPERATION 1: n8n Methodology Generation
        // ============================================
        operations.push((async () => {
            updateProgress('methodology', {
                status: 'running',
                progress: 10,
                message: 'Sending to AI workflow...',
                startTime: Date.now()
            });
            
            try {
                // Use polling approach - trigger n8n and poll database for results
                const methodologyResult = await generateMethodologyWithPolling(
                    pdfBase64,
                    sessionId,
                    userEmail,
                    file.name,
                    (progressValue, message) => {
                        updateProgress('methodology', {
                            progress: progressValue,
                            message
                        });
                    }
                );
                
                result.methodology = methodologyResult;
                updateProgress('methodology', {
                    status: 'complete',
                    progress: 100,
                    message: `Complete! Score: ${methodologyResult.alignmentScore}%`,
                    endTime: Date.now()
                });
                
            } catch (error) {
                result.errors.methodology = error instanceof Error ? error.message : 'Unknown error';
                updateProgress('methodology', {
                    status: 'error',
                    progress: 0,
                    message: result.errors.methodology,
                    endTime: Date.now()
                });
            }
        })());
        
        // ============================================
        // OPERATION 2 & 3: ToR Extraction → CV Matching
        // ============================================
        operations.push((async () => {
            // First extract ToR requirements
            updateProgress('torExtraction', {
                status: 'running',
                progress: 20,
                message: 'Extracting consultant requirements...',
                startTime: Date.now()
            });
            
            try {
                const torResult = await extractTorForCVs(file);
                result.torExtraction = torResult;
                
                updateProgress('torExtraction', {
                    status: 'complete',
                    progress: 100,
                    message: 'Requirements extracted!',
                    endTime: Date.now()
                });
                
                // Now run both CV searches in parallel
                const cvOperations: Promise<void>[] = [];
                
                // Associate CV Search
                cvOperations.push((async () => {
                    updateProgress('associateCVs', {
                        status: 'running',
                        progress: 20,
                        message: 'Searching Associate CVs...',
                        startTime: Date.now()
                    });
                    
                    try {
                        const associateResult = await matchCVsFromTor(
                            { requirements: torResult.requirements },
                            torResult.query_vector,
                            20, // Top 20 CVs
                            'associate'
                        );
                        
                        result.associateCVs = associateResult;
                        updateProgress('associateCVs', {
                            status: 'complete',
                            progress: 100,
                            message: `Found ${associateResult.matches?.length || 0} matching Associates`,
                            endTime: Date.now()
                        });
                        
                    } catch (error) {
                        result.errors.associateCVs = error instanceof Error ? error.message : 'Unknown error';
                        updateProgress('associateCVs', {
                            status: 'error',
                            progress: 0,
                            message: result.errors.associateCVs,
                            endTime: Date.now()
                        });
                    }
                })());
                
                // IOD Staff CV Search
                cvOperations.push((async () => {
                    updateProgress('iodStaffCVs', {
                        status: 'running',
                        progress: 20,
                        message: 'Searching IOD PARC Staff CVs...',
                        startTime: Date.now()
                    });
                    
                    try {
                        const iodResult = await matchCVsFromTor(
                            { requirements: torResult.requirements },
                            torResult.query_vector,
                            20, // Top 20 CVs
                            'iod_staff'
                        );
                        
                        result.iodStaffCVs = iodResult;
                        updateProgress('iodStaffCVs', {
                            status: 'complete',
                            progress: 100,
                            message: `Found ${iodResult.matches?.length || 0} matching IOD Staff`,
                            endTime: Date.now()
                        });
                        
                    } catch (error) {
                        result.errors.iodStaffCVs = error instanceof Error ? error.message : 'Unknown error';
                        updateProgress('iodStaffCVs', {
                            status: 'error',
                            progress: 0,
                            message: result.errors.iodStaffCVs,
                            endTime: Date.now()
                        });
                    }
                })());
                
                // Wait for both CV searches
                await Promise.all(cvOperations);
                
            } catch (error) {
                result.errors.torExtraction = error instanceof Error ? error.message : 'Unknown error';
                updateProgress('torExtraction', {
                    status: 'error',
                    progress: 0,
                    message: result.errors.torExtraction,
                    endTime: Date.now()
                });
                
                // Mark CV searches as blocked
                updateProgress('associateCVs', {
                    status: 'error',
                    message: 'Blocked: ToR extraction failed'
                });
                updateProgress('iodStaffCVs', {
                    status: 'error',
                    message: 'Blocked: ToR extraction failed'
                });
            }
        })());
        
        // Wait for all operations to complete
        await Promise.all(operations);
        
        console.log('[Proposal Assistant] All operations complete:', result);
        return result;
        
    } catch (error) {
        console.error('[Proposal Assistant] Fatal error:', error);
        throw error;
    }
}

/**
 * Fetch methodology result from database by session ID
 * (For cases where we need to retrieve results later)
 */
export async function getMethodologyBySessionId(sessionId: string): Promise<any> {
    try {
        const response = await axios.get(`${API_URL}/proposal-section/${sessionId}`);
        return response.data;
    } catch (error) {
        console.error('[Proposal Assistant] Failed to fetch methodology:', error);
        return null;
    }
}

/**
 * Run the Proposal Assistant with pre-extracted requirements and custom tiers
 * 
 * This version allows the user to review and customize requirements before processing.
 * 
 * @param file - PDF file to process
 * @param userEmail - User's email
 * @param torRequirements - Pre-extracted ToR requirements
 * @param tiers - Requirement tier settings
 * @param selectedItems - Selected items per category
 * @param onProgress - Callback for progress updates
 * @param enableCVSearch - Whether to run CV search (default: true)
 * @returns Combined results from all operations
 */
export async function runProposalAssistantWithRequirements(
    file: File,
    userEmail: string,
    torRequirements: TorForCVsExtractionResult,
    tiers: RequirementTiers,
    selectedItems: { [category: string]: Set<string> },
    onProgress: (progress: ProposalAssistantProgress) => void,
    tieredItems?: { [category: string]: Array<{ text: string; tier: string }> },
    enableCVSearch: boolean = true
): Promise<ProposalAssistantResult> {
    const sessionId = generateSessionId();
    
    // Initialize progress state
    const progress: ProposalAssistantProgress = {
        torExtraction: { status: 'complete', progress: 100, message: 'Requirements ready!' },
        methodology: { status: 'idle', progress: 0, message: 'Waiting to start...' },
        associateCVs: { status: 'idle', progress: 0, message: 'Waiting to start...' },
        iodStaffCVs: { status: 'idle', progress: 0, message: 'Waiting to start...' }
    };
    
    // Initialize result
    const result: ProposalAssistantResult = {
        sessionId, // Include for retry functionality
        methodology: null,
        associateCVs: null,
        iodStaffCVs: null,
        torExtraction: torRequirements,
        errors: {}
    };
    
    // Helper to update progress
    const updateProgress = (
        key: keyof ProposalAssistantProgress,
        update: Partial<ProgressState>
    ) => {
        progress[key] = { ...progress[key], ...update };
        onProgress({ ...progress });
    };
    
    // Build filtered requirements with tiers and selected items
    // Use V2 tieredItems if provided, otherwise fall back to legacy category-level tiers
    const filteredRequirements = tieredItems 
        ? buildFilteredRequirementsV2(torRequirements.requirements, tieredItems)
        : buildFilteredRequirements(torRequirements.requirements, tiers, selectedItems);
    
    try {
        // Convert PDF to base64 for n8n
        console.log('[Proposal Assistant] Converting PDF to base64...');
        const pdfBase64 = await fileToBase64(file);
        console.log(`[Proposal Assistant] PDF converted: ${pdfBase64.length} chars`);
        
        // Start all operations in parallel
        const operations: Promise<void>[] = [];
        
        // ============================================
        // OPERATION 1: n8n Methodology Generation
        // ============================================
        operations.push((async () => {
            updateProgress('methodology', {
                status: 'running',
                progress: 10,
                message: 'Sending to AI workflow...',
                startTime: Date.now()
            });
            
            try {
                const methodologyResult = await generateMethodologyWithPolling(
                    pdfBase64,
                    sessionId,
                    userEmail,
                    file.name,
                    (progressValue, message) => {
                        updateProgress('methodology', { progress: progressValue, message });
                    }
                );
                
                result.methodology = methodologyResult;
                updateProgress('methodology', {
                    status: 'complete',
                    progress: 100,
                    message: `Complete! Score: ${methodologyResult.alignmentScore}%`,
                    endTime: Date.now()
                });
                
            } catch (error) {
                result.errors.methodology = error instanceof Error ? error.message : 'Unknown error';
                updateProgress('methodology', {
                    status: 'error',
                    progress: 0,
                    message: result.errors.methodology,
                    endTime: Date.now()
                });
            }
        })());
        
        // ============================================
        // OPERATION 2 & 3: CV Matching (only if enabled)
        // ============================================
        if (enableCVSearch) {
            // Run CV matching SEQUENTIALLY to avoid 45-second SWA proxy timeout
            // Each call processes 5 CVs in ~20-25 seconds
            
            // IOD Staff CV Matching (run first - smaller pool, ~26 CVs)
            updateProgress('iodStaffCVs', {
                status: 'running',
                progress: 20,
                message: 'Searching IOD PARC Staff CVs...',
                startTime: Date.now()
            });
            
            try {
                const iodResult = await matchCVsFromTor(
                    { requirements: filteredRequirements },
                    torRequirements.query_vector,
                    20,
                    'iod_staff'
                );
                
                result.iodStaffCVs = iodResult;
                updateProgress('iodStaffCVs', {
                    status: 'complete',
                    progress: 100,
                    message: `Found ${iodResult.matches?.length || 0} matching IOD Staff`,
                    endTime: Date.now()
                });
                
            } catch (error) {
                result.errors.iodStaffCVs = error instanceof Error ? error.message : 'Unknown error';
                updateProgress('iodStaffCVs', {
                    status: 'error',
                    progress: 0,
                    message: result.errors.iodStaffCVs,
                    endTime: Date.now()
                });
            }
            
            // Associate CV Matching (run second - larger pool)
            updateProgress('associateCVs', {
                status: 'running',
                progress: 20,
                message: 'Searching Associate CVs...',
                startTime: Date.now()
            });
            
            try {
                const associateResult = await matchCVsFromTor(
                    { requirements: filteredRequirements },
                    torRequirements.query_vector,
                    20,
                    'associate'
                );
                
                result.associateCVs = associateResult;
                updateProgress('associateCVs', {
                    status: 'complete',
                    progress: 100,
                    message: `Found ${associateResult.matches?.length || 0} matching Associates`,
                    endTime: Date.now()
                });
                
            } catch (error) {
                result.errors.associateCVs = error instanceof Error ? error.message : 'Unknown error';
                updateProgress('associateCVs', {
                    status: 'error',
                    progress: 0,
                    message: result.errors.associateCVs,
                    endTime: Date.now()
                });
            }
        } else {
            // CV search skipped - mark as idle with skip message
            updateProgress('iodStaffCVs', {
                status: 'complete',
                progress: 100,
                message: 'Skipped - search later from results',
                endTime: Date.now()
            });
            updateProgress('associateCVs', {
                status: 'complete',
                progress: 100,
                message: 'Skipped - search later from results',
                endTime: Date.now()
            });
        }
        
        // Wait for methodology operation to complete (CV matching already done sequentially)
        await Promise.all(operations);
        
        console.log('[Proposal Assistant] All operations complete:', result);
        return result;
        
    } catch (error) {
        console.error('[Proposal Assistant] Fatal error:', error);
        throw error;
    }
}

/**
 * Build filtered requirements object with V2 per-item tiers
 */
export function buildFilteredRequirementsV2(
    requirements: any,
    tieredItems: { [category: string]: Array<{ text: string; tier: string }> }
): any {
    const filtered: any = { ...requirements };
    
    // Helper to build category with items split by tier
    // Returns empty structure for empty arrays (to clear removed items)
    const buildCategory = (items: Array<{ text: string; tier: string }> | undefined) => {
        const emptyCategory = { items: [], required: [], preferred: [], bonus: [] };
        if (!items) return null; // undefined = not modified, keep original
        if (items.length === 0) return emptyCategory; // empty array = cleared by user
        return {
            items: items.map(i => ({ text: i.text, tier: i.tier })),
            required: items.filter(i => i.tier === 'required').map(i => i.text),
            preferred: items.filter(i => i.tier === 'preferred').map(i => i.text),
            bonus: items.filter(i => i.tier === 'bonus').map(i => i.text)
        };
    };
    
    // Helper to apply category - replaces original if tieredItems has the category
    const applyCategory = (targetKey: string, tieredKey: string, altTargetKey?: string) => {
        if (tieredItems[tieredKey] !== undefined) {
            const cat = buildCategory(tieredItems[tieredKey]);
            if (cat !== null) {
                filtered[targetKey] = cat;
            }
        }
    };
    
    // Geographic
    if (tieredItems.geographic_countries || tieredItems.geographic_regions) {
        filtered.geographic_experience = {
            ...filtered.geographic_experience,
            countries: tieredItems.geographic_countries ? buildCategory(tieredItems.geographic_countries) : filtered.geographic_experience?.countries,
            regions: tieredItems.geographic_regions ? buildCategory(tieredItems.geographic_regions) : filtered.geographic_experience?.regions,
            // Also set legacy format for backward compatibility
            required_countries: tieredItems.geographic_countries?.filter(i => i.tier === 'required').map(i => i.text) || [],
            preferred_countries: tieredItems.geographic_countries?.filter(i => i.tier === 'preferred').map(i => i.text) || [],
            required_regions: tieredItems.geographic_regions?.filter(i => i.tier === 'required').map(i => i.text) || [],
            preferred_regions: tieredItems.geographic_regions?.filter(i => i.tier === 'preferred').map(i => i.text) || []
        };
    }
    
    // ===== 9 ALIGNED CATEGORIES (V3) =====
    
    // 2. Methods → methods_methodologies
    if (tieredItems.methods !== undefined) {
        const cat = buildCategory(tieredItems.methods);
        if (cat !== null) {
            filtered.methods_methodologies = cat;
        }
    }
    
    // 3. Sectors → sector_experience
    if (tieredItems.sectors !== undefined) {
        const cat = buildCategory(tieredItems.sectors);
        if (cat !== null) {
            filtered.sector_experience = cat;
        }
    }
    
    // 4. Tools → technical_tools_software
    if (tieredItems.tools !== undefined) {
        const cat = buildCategory(tieredItems.tools);
        if (cat !== null) {
            filtered.technical_tools_software = cat;
        }
    }
    
    // 5. Frameworks → frameworks_standards
    if (tieredItems.frameworks !== undefined) {
        const cat = buildCategory(tieredItems.frameworks);
        if (cat !== null) {
            filtered.frameworks_standards = cat;
        }
    }
    
    // 6. Languages → language_requirements
    if (tieredItems.languages !== undefined) {
        const cat = buildCategory(tieredItems.languages);
        if (cat !== null) {
            filtered.language_requirements = cat;
        }
    }
    
    // 7. Donors → donor_experience
    if (tieredItems.donors !== undefined) {
        const cat = buildCategory(tieredItems.donors);
        if (cat !== null) {
            filtered.donor_experience = cat;
        }
    }
    
    // Experience Requirements (years)
    if (tieredItems.experience_years && tieredItems.experience_years.length > 0) {
        const expItem = tieredItems.experience_years[0];
        // Parse "10+ years" format to get the number
        const yearsMatch = expItem.text.match(/(\d+)/);
        const years = yearsMatch ? parseInt(yearsMatch[1]) : null;
        filtered.experience_requirements = {
            ...filtered.experience_requirements,
            minimum_years: years,
            minimum_years_tier: expItem.tier
        };
    }
    
    // Education Requirements
    if (tieredItems.education && tieredItems.education.length > 0) {
        const eduItem = tieredItems.education[0];
        filtered.education_requirements = {
            ...filtered.education_requirements,
            minimum_level: eduItem.text,
            minimum_level_tier: eduItem.tier
        };
    }
    
    // Add V2 flag so backend knows to use per-item tiers
    filtered._v2_format = true;
    
    console.log('[Proposal Assistant] Built V2 filtered requirements:', filtered);
    return filtered;
}

/**
 * Build filtered requirements object with tiers and selected items (legacy V1)
 */
function buildFilteredRequirements(
    requirements: any,
    tiers: RequirementTiers,
    selectedItems: { [category: string]: Set<string> }
): any {
    const filtered = { ...requirements, requirement_tiers: tiers };
    
    // Filter geographic countries
    if (filtered.geographic_experience && selectedItems.geographic_countries) {
        filtered.geographic_experience.required_countries = Array.from(selectedItems.geographic_countries);
    }
    
    // Filter geographic regions
    if (filtered.geographic_experience && selectedItems.geographic_regions) {
        filtered.geographic_experience.required_regions = Array.from(selectedItems.geographic_regions);
    }
    
    // Filter methods & methodologies
    const methodsField = filtered.methods_methodologies || filtered.research_evaluation_skills;
    if (methodsField && selectedItems.research_evaluation) {
        if (filtered.methods_methodologies) {
            filtered.methods_methodologies.required = Array.from(selectedItems.research_evaluation);
        } else if (filtered.research_evaluation_skills) {
            filtered.research_evaluation_skills.required = Array.from(selectedItems.research_evaluation);
        }
    }
    
    // Filter technical domain expertise
    if (filtered.technical_domain_expertise && selectedItems.technical_domain) {
        filtered.technical_domain_expertise.required = Array.from(selectedItems.technical_domain);
    }
    
    // Filter technical tools & software
    const toolsField = filtered.technical_tools_software || filtered.analytical_tools;
    if (toolsField && selectedItems.analytical_tools) {
        if (filtered.technical_tools_software) {
            filtered.technical_tools_software.required = Array.from(selectedItems.analytical_tools);
        } else if (filtered.analytical_tools) {
            filtered.analytical_tools.required = Array.from(selectedItems.analytical_tools);
        }
    }
    
    // Filter frameworks & standards
    const frameworksField = filtered.frameworks_standards || filtered.frameworks_methodologies;
    if (frameworksField && selectedItems.frameworks) {
        if (filtered.frameworks_standards) {
            filtered.frameworks_standards.required = Array.from(selectedItems.frameworks);
        } else if (filtered.frameworks_methodologies) {
            filtered.frameworks_methodologies.required = Array.from(selectedItems.frameworks);
        }
    }
    
    // Filter communication
    if (filtered.communication_stakeholder && selectedItems.communication) {
        filtered.communication_stakeholder.required = Array.from(selectedItems.communication);
    }
    
    // Filter professional & ethical
    if (filtered.professional_ethical && selectedItems.professional_ethical) {
        filtered.professional_ethical.required = Array.from(selectedItems.professional_ethical);
    }
    
    // Filter languages
    if (filtered.language_requirements && selectedItems.languages) {
        filtered.language_requirements.required = Array.from(selectedItems.languages);
    }
    
    // Filter sectors
    if (filtered.sector_experience && selectedItems.sectors) {
        filtered.sector_experience.required = Array.from(selectedItems.sectors);
    }
    
    return filtered;
}
